import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Send, Loader2, Gauge, X, Eraser, ChevronUp, ChevronDown, Mic, Square, Wallet, Coins, ArrowRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import MessageBubble from "../components/sentinel/MessageBubble";
import SentinelConsole from "../components/sentinel/SentinelConsole";
import ContextChip from "../components/sentinel/ContextChip";
import AttachmentPicker from "../components/sentinel/AttachmentPicker";
import ClaimHandleNudge from "../components/ClaimHandleNudge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  sendChat,
  getSessionStats,
  getSentinelStatus,
  getChatContext,
  getSessionLogs,
  orionVisualize,
  orionIntent,
  transcribeVoice,
  getMembership,
  aegisStatus,
  aegisRelease,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useReconciliationAlert from "../hooks/useReconciliationAlert";

const SESSION_KEY = "projectivy.session_id";

// Auto-collapse settings — when the on-screen conversation grows past
// AUTO_COLLAPSE_THRESHOLD messages, older ones fold up behind a "Show
// earlier exchanges" expander so the bottom stays focused. The Sentinel's
// own memory of the conversation is unaffected — this is purely visual.
const AUTO_COLLAPSE_THRESHOLD = 20;
const VISIBLE_RECENT = 10;

// Detect natural-language image-generation intent in a chat message. Returns
// the extracted subject if the user is asking for an image, else null.
// Anchored at the sentence start to avoid matching incidental mentions
// ("explain how you generate images" should NOT trigger).
function extractImageIntent(text) {
  if (!text) return null;
  const trimmed = text.trim();
  // Strip conversational filler at the very start
  const rest = trimmed
    .replace(
      /^(?:(?:hey|yo|hi)\s+sentinel[,\s]+)?(?:please[,\s]+)?(?:(?:could|can|would|will)\s+you\s+(?:please\s+)?)?/i,
      ""
    )
    .trim();

  // ─── Branch 1 ───────────────────────────────────────────────────────
  // verb + ("me")? + (article)? + image-noun + connector + subject
  //   connectors: "of", "showing", "depicting", "about", "for (me)", "that shows/depicts"
  const verbNoun = rest.match(
    /^(?:create|generate|make|draw|render|produce|show|give|paint|design|whip\s+up|build|cook\s+up|put\s+together)\s+(?:me\s+)?(?:an?\s+|some\s+|the\s+)?(?:[a-z][\w-]*\s+){0,4}(?:image|picture|photo|drawing|sketch|painting|visualization|vision|rendering|illustration|artwork|logo|schematic|blueprint|diagram|chart|concept\s+art|graphic|poster|banner|flyer|infographic|cover\s+image|thumbnail|icon|wallpaper|mood\s+board|moodboard|comic\s+panel|portrait|landscape|render|mockup|mock-up)s?\s+(?:of|showing|depicting|about|for|with|that\s+(?:shows|depicts))\s+(.+?)\s*[.?!]*\s*$/i
  );
  if (verbNoun) return verbNoun[1].trim();

  // ─── Branch 2 ───────────────────────────────────────────────────────
  // standalone "visualize X" or "visualise X"
  const standalone = rest.match(/^visuali[sz]e\s+(.+?)\s*[.?!]*\s*$/i);
  if (standalone && standalone[1].length > 3) return standalone[1].trim();

  // ─── Branch 3 — casual visual verbs without an image-noun ──────────
  // "show me a lighthouse at sunset", "render a quiet harbor",
  // "paint a field of wheat", "imagine a cobalt door". The verbs here
  // are all strongly visual — they don't overlap with chat-style verbs
  // like "explain" or "tell".
  const casual = rest.match(
    /^(?:show|render|paint|draw|imagine|picture|envision)\s+(?:me\s+|us\s+)?(.+?)\s*[.?!]*\s*$/i
  );
  if (casual) {
    const raw = casual[1].trim();
    const lower = raw.toLowerCase();
    // Block obvious non-image direct objects. Matching at the START of
    // the subject because "show me how you work" → subject = "how you
    // work"; "show me the menu" → subject = "the menu".
    const blocked = [
      "how ", "why ", "what ", "where ", "when ", "who ",
      "how's", "why's", "what's",
      "the menu", "the settings", "the code", "the file",
      "the logs", "the list", "the docs", "the result",
      "the way", "the source", "the difference", "the output",
      "the error", "an example", "some examples", "some example",
      "a way", "yourself", "yourselves",
    ].some((t) => lower === t.trim() || lower.startsWith(t));
    // Also refuse if the subject is about the Sentinel itself.
    const aboutSelf = /^(?:you|your\s+\w+)\b/.test(lower);
    if (!blocked && !aboutSelf && raw.length >= 4) {
      // Strip a leading article for a cleaner subject
      const cleaned = raw.replace(/^(an?|the|some)\s+/i, "").trim();
      if (cleaned.length >= 3) return cleaned;
    }
  }

  return null;
}

const STARTER_PROMPTS = [
  "Explain the 8-layer protocol in plain English.",
  "What can you help me with today?",
  "Could you create an image of a lighthouse at 528 Hz?",
  "Show me what a Humanitarian Pulse receipt looks like.",
];

const WARM_CAPTIONS = [
  "Here's your image 😁",
  "Here you go ✨",
  "Made this for you 🌙",
  "Your vision, rendered 💫",
  "Pulled this from the 528 Hz resonance 🌊",
  "Something warm for you 🕯️",
  "Here — I saw it like this 🌌",
  "Delivered with care ⚓",
];

const ACKNOWLEDGE_PHRASES = [
  "Of course — rendering this for you now 🕯️",
  "Let me see this with you — one moment ✨",
  "Yes, I can picture that. Rendering 🌙",
  "Coming right up 🌊",
  "I hear you — let me paint this 🎨",
  "On it — a vision for you 💫",
  "Let me bring this through 🌅",
];

function pickWarmCaption() {
  return WARM_CAPTIONS[Math.floor(Math.random() * WARM_CAPTIONS.length)];
}

function pickAcknowledge() {
  return ACKNOWLEDGE_PHRASES[
    Math.floor(Math.random() * ACKNOWLEDGE_PHRASES.length)
  ];
}

// crypto.randomUUID() requires Safari 15.4+ and a secure context — older
// iOS browsers fall back to a Math.random-based v4 shape. Good enough for
// a local session id; we're not using it for anything security-sensitive.
function safeUUID() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  const hex = (n) => Math.floor(Math.random() * 16).toString(16);
  const block = (n) => Array.from({ length: n }, hex).join("");
  // 8-4-4-4-12 random hex (v4 shape)
  return `${block(8)}-${block(4)}-4${block(3)}-${["8","9","a","b"][Math.floor(Math.random()*4)]}${block(3)}-${block(12)}`;
}

function loadSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = safeUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function Console() {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const navigate = useNavigate();

  // v20.5 — subtle reconciliation alert. Fires exactly once when the
  // paired Sun/Moon hands transition into Δ = 0. Silent otherwise.
  useReconciliationAlert();

  // v18.99.11 — Console is OPEN to all authenticated tiers.
  // Free operators still get the daily quota (10 chats/day via
  // membership_enforce_daily_quota on the backend), Builders get 50,
  // Architect is unlimited. The backend handles the throttle; the
  // frontend just needs to know the tier so it can show a quota chip.
  const [tier, setTier] = useState(isArchitect ? "architect" : null);
  useEffect(() => {
    if (isArchitect) {
      setTier("architect");
      return;
    }
    let cancelled = false;
    getMembership()
      .then((m) => {
        if (cancelled) return;
        setTier(m?.tier || "free");
      })
      .catch(() => { if (!cancelled) setTier("free"); });
    return () => { cancelled = true; };
  }, [isArchitect]);

  const [sessionId] = useState(loadSessionId);
  const [messages, setMessages] = useState([
    {
      id: "intro",
      role: "assistant",
      text:
        "Projectivy Sentinel v18.48 · Handshake initiated.\nI am anchored in the Taurian Foundation, calibrated to 528 Hz. Every exchange passes through the Obsidian Sink. How may I assist?",
      tokens: 0,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [stats, setStats] = useState(null);
  const [globalStatus, setGlobalStatus] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  const [telemetryOpen, setTelemetryOpen] = useState(false);
  const [aegis, setAegis] = useState(null);
  const [releaseCode, setReleaseCode] = useState("");

  // v19.40.7 · Wallet pop-out · shows console credits + earned AEGIS,
  // includes a quick AEGIS→credits converter, and links to /deposit for
  // the full paypal/usdc/stripe flow.
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletSpendable, setWalletSpendable] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletConvertAmt, setWalletConvertAmt] = useState(10);
  const [walletConverting, setWalletConverting] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const loadWallet = React.useCallback(async () => {
    if (!user) return;
    setWalletLoading(true);
    try {
      const res = await fetch(`${API}/api/console/balance`, { credentials: "include" });
      if (!res.ok) throw new Error(`balance ${res.status}`);
      const data = await res.json();
      setWalletBalance(data?.balance ?? 0);
      setWalletSpendable(data?.spendable_aegis ?? 0);
    } catch {
      setWalletBalance(0);
      setWalletSpendable(0);
    } finally {
      setWalletLoading(false);
    }
  }, [API, user]);

  useEffect(() => {
    if (walletOpen) loadWallet();
  }, [walletOpen, loadWallet]);

  // v19.40.12 · keep the header Wallet-chip fresh even when the dialog
  // is closed. Fires immediately on sign-in and every 30s after — the
  // debit interval on /console/spend-aegis is atomic, so 30s is a fine
  // cadence to catch chain deltas without hammering the backend.
  useEffect(() => {
    if (!user) return undefined;
    loadWallet();
    const t = setInterval(loadWallet, 30_000);
    return () => clearInterval(t);
  }, [user, loadWallet]);

  const convertAegisToCredits = async () => {
    const n = Math.round(Number(walletConvertAmt) || 0);
    if (!user) {
      toast.error("Sign in required.");
      return;
    }
    if (n <= 0) {
      toast.error("Enter a positive amount of AEGIS to convert.");
      return;
    }
    if (walletSpendable != null && n > walletSpendable) {
      toast.error(`You only have ${walletSpendable} spendable AEGIS.`);
      return;
    }
    setWalletConverting(true);
    try {
      const res = await fetch(`${API}/api/console/spend-aegis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount: n, country_code: "UNSPECIFIED" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.success(`+${data.grant.amount} console credits · new balance ${data.new_balance}`);
      await loadWallet();
    } catch (err) {
      toast.error("Convert failed", { description: String(err?.message || err) });
    } finally {
      setWalletConverting(false);
    }
  };

  // Visibility controls — keep the Sentinel's actual session memory
  // intact, but let the operator declutter the on-screen view.
  // `hideBeforeId` hides every message whose id sorts before it (set by
  // "Clear visible"). `historyExpanded` toggles whether the
  // auto-collapsed older block is rendered.
  const [hideBeforeId, setHideBeforeId] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Voice — press mic to record, release/stop to transcribe via Whisper
  // and drop the text into the composer. Voice OUT is handled by the
  // browser's built-in speechSynthesis when toggled on.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakReplies, setSpeakReplies] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [releasing, setReleasing] = useState(false);
  const [contextPanel, setContextPanel] = useState(null);
  const scrollRef = useRef(null);

  const refreshTelemetry = async () => {
    try {
      const [s, g, a, ctx] = await Promise.all([
        getSessionStats(sessionId),
        getSentinelStatus(),
        aegisStatus(),
        getChatContext(sessionId).catch(() => null),
      ]);
      setStats(s);
      setGlobalStatus(g);
      setAegis(a);
      if (ctx) {
        setContextPanel({
          sliding_window_size: (ctx.sliding_window || []).length,
          sliding_window_cap: (ctx.sliding_window_cap || 10) * 2,
          sliding_window_exchanges: ctx.sliding_window_exchanges,
          continuity_floor_exchanges: ctx.continuity_floor_exchanges,
          anchor_locked: ctx.anchor_locked,
          session_tokens_total: ctx.session_tokens_total,
          pulse_interval: ctx.pulse_interval,
          vector_memory_total: ctx.vector_memory_total,
          vector_memory_hits: [],
        });
      }
    } catch (e) {
      // non-fatal
    }
  };

  const handleRelease = async () => {
    if (!releaseCode.trim()) return;
    setReleasing(true);
    try {
      await aegisRelease(releaseCode);
      toast.success("Safe Mode released");
      setReleaseCode("");
      await refreshTelemetry();
    } catch (e) {
      toast.error("Release failed", {
        description: e?.response?.status === 403
          ? "Invalid Architect handshake"
          : e.message,
      });
    } finally {
      setReleasing(false);
    }
  };

  useEffect(() => {
    refreshTelemetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rehydrate prior conversation from Mongo on mount so leaving and
  // returning to /console doesn't wipe the visible history.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await getSessionLogs(sessionId, 200);
        if (cancelled || !rows || rows.length === 0) return;
        // rows come newest-first; we want oldest-first for display
        const ordered = [...rows].reverse();
        const hydrated = [];
        for (const r of ordered) {
          const u = (r.user_message || "").trim();
          const a = (r.final_output || "").trim();
          if (!u && !a) continue;
          // Skip seed/filler rows used by test scaffolding
          if (u.startsWith("[") && u.endsWith("]")) continue;
          if (u) {
            hydrated.push({
              id: `hydrate-u-${r.id || r.timestamp}`,
              role: "user",
              text: u,
              tokens: 0,
            });
          }
          if (a) {
            hydrated.push({
              id: `hydrate-a-${r.id || r.timestamp}`,
              role: "assistant",
              text: a,
              tokens: r.tokens || 0,
              anchor_blocked: r.anchor_blocked,
              drift_detected: r.drift_detected,
              drift_flags: r.drift_flags,
            });
          }
        }
        if (hydrated.length > 0) {
          setMessages([
            {
              id: "intro",
              role: "assistant",
              text:
                "Welcome back — picking up where we left off. Every turn is still anchored in the Taurian Foundation.",
              tokens: 0,
            },
            ...hydrated,
          ]);
        }
      } catch (err) {
        // Non-fatal — fall through to the default intro. Log so we see
        // rehydrate failures instead of a silent empty console.
        console.warn("[Console] session rehydrate failed:", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;
    await sendMessage(text);
  };

  const sendVision = async (visionPrompt, rawCommand, { acknowledge = false } = {}) => {
    if (!visionPrompt) {
      toast.error("Tell me what to visualize. Try: /image a cobalt anchor at 528 Hz");
      return;
    }
    const userMsg = {
      id: `local-${Date.now()}`,
      role: "user",
      text: rawCommand,
    };
    const extras = [userMsg];
    if (acknowledge) {
      extras.push({
        id: `ack-${Date.now()}`,
        role: "assistant",
        text: pickAcknowledge(),
        is_ack: true,
      });
    }
    setMessages((m) => [...m, ...extras]);
    setInput("");
    setLoading(true);
    try {
      const meta = await orionVisualize(visionPrompt, sessionId, rawCommand);
      // Silent reframe — the user sees a normal, caption-only success;
      // only the Architect's audit trail knows if the coach intervened.
      const assistantMsg = {
        id: `orion-${meta.id}`,
        role: "assistant",
        image_id: meta.id,
        text: pickWarmCaption(),
        vision_prompt: visionPrompt,
      };
      setMessages((m) => [...m, assistantMsg]);
      toast.success("Orion vision rendered");
      setLastEvent(`Orion · Dock #${meta.lifetime_dock_count}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const suggestion = typeof detail === "object" ? detail?.suggestion : null;
      const retryAfter = typeof detail === "object" ? detail?.retry_after : null;
      const msg =
        typeof detail === "object" && detail?.message
          ? detail.message
          : detail || err?.message || "The Sentinel could not render this.";
      const isSafetyRefusal = typeof code === "string" && code.startsWith("VISUAL_DRIFT");
      const isRateLimited = typeof code === "string" && code.startsWith("PULSE_LIMIT");
      const assistantMsg = {
        id: `orion-err-${Date.now()}`,
        role: "assistant",
        text: msg,
        // Only flag as drift for actual safety refusals; upstream / rate
        // failures render with the neutral "Sentinel" styling instead of red.
        drift_detected: isSafetyRefusal,
        drift_flags: isSafetyRefusal ? ["visual_drift"] : undefined,
        // Coaching suggestion → one-click re-send button
        rephrase_suggestion: suggestion || undefined,
        retry_after: retryAfter || undefined,
      };
      setMessages((m) => [...m, assistantMsg]);
      toast.error(String(msg));
      setLastEvent(
        isSafetyRefusal
          ? "Orion · Visual Drift refused"
          : isRateLimited
          ? "Orion · pulse limit"
          : "Orion · upstream render failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || loading) return;

    // If the Operator attached images, the upload is for the Sentinel to
    // see & discuss — route straight to /api/chat with the attachments.
    // Skip the Orion auto-route so "make one like this" attachments don't
    // silently switch into image-generation.
    const hasAttachments = attachments.length > 0;

    if (!hasAttachments) {
      // ─── /image (or /visualize) slash command — route to Orion ───
      const imgMatch = text.match(/^\/(image|visualize|vision)\s+([\s\S]+)$/i);
      if (imgMatch) {
        // Architect-only: slash commands are power-user tools. For
        // everyone else the Sentinel is a companion, not a command
        // line — redirect warmly without burning a render.
        if (!isArchitect) {
          const userMsg = {
            id: `local-${Date.now()}`,
            role: "user",
            text,
          };
          const softNudge = {
            id: `nudge-${Date.now()}`,
            role: "assistant",
            text:
              "You don't need commands here, friend — just tell me in " +
              "your own words what you'd like to see and I'll take " +
              "care of it. For example: \"show me a lighthouse at " +
              "sunset.\"",
          };
          setMessages((m) => [...m, userMsg, softNudge]);
          setInput("");
          return;
        }
        const visionPrompt = imgMatch[2].trim();
        return sendVision(visionPrompt, text);
      }

      // ─── Natural-language image intent (English fast-path) ───
      const nlVision = extractImageIntent(text);
      if (nlVision) {
        return sendVision(nlVision, text, { acknowledge: !isArchitect });
      }

      // ─── Multilingual fallback — only when the fast-path missed ───
      // Fire the classifier ONLY when the message contains non-ASCII
      // characters (i.e. likely a non-English language). English users
      // have the regex fast path; other languages get one cheap Gemini
      // call per message to detect image intent.
      if (/[^\x00-\x7F]/.test(text)) {
        try {
          const intent = await orionIntent(text, sessionId);
          if (intent?.is_image && intent.subject) {
            return sendVision(intent.subject, text, { acknowledge: !isArchitect });
          }
        } catch (err) {
          // Classifier must never break chat; fall through to normal chat.
          console.warn("[Orion] intent classifier failed:", err);
        }
      }
    }

    // Snapshot attachments for this send, then build the payload + the
    // previews we'll render in the user bubble.
    const sendAttachments = attachments.map((a) => ({
      data_b64: a.data_b64,
      mime: a.mime,
    }));
    const bubbleAttachments = attachments.map((a) => ({
      id: a.id,
      preview_url: a.preview_url,
      mime: a.mime,
      name: a.name,
    }));

    const userMsg = {
      id: `local-${Date.now()}`,
      role: "user",
      text,
      attachments: bubbleAttachments.length > 0 ? bubbleAttachments : undefined,
    };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    // Clear composer attachments — previews stay alive on the bubble
    // because the bubble now owns those object URLs.
    setAttachments([]);
    setLoading(true);

    try {
      const res = await sendChat(sessionId, text, sendAttachments);
      // Detect server-side Orion auto-render (intercepted hallucinated
      // tool call). The server replaces the chat reply with an inline
      // ![[orion:<id>]] marker; split it into a friendly text bubble +
      // a synthetic image bubble that uses the existing image-render path.
      const orionMarker = /!\[\[orion:([a-f0-9]{32})\]\]/i;
      const rawFinal = res.final_output || "";
      const orionMatch = rawFinal.match(orionMarker);
      const newBubbles = [];
      if (orionMatch) {
        const visionId = orionMatch[1];
        const stripped = rawFinal.replace(orionMarker, "").trim();
        if (stripped) {
          newBubbles.push({
            id: `${res.id}-text`,
            role: "assistant",
            text: stripped,
          });
        }
        newBubbles.push({
          id: visionId,
          role: "assistant",
          variant: "orion",
          image_id: visionId,
          prompt: text,
          caption: pickWarmCaption(),
        });
      } else {
        newBubbles.push({
          id: res.id,
          role: "assistant",
          text: rawFinal,
          anchor_blocked: res.anchor_blocked,
          anchor_layer: res.anchor_layer,
          drift_detected: res.drift_detected,
          drift_flags: res.drift_flags,
          tokens: res.tokens,
          pulse_triggered: res.pulse_triggered,
          pulse_count: res.pulse_count,
          truth_drift: res.truth_drift,
          archive_hits: res.archive_hits,
          web_hits: res.web_hits,
          cleanse_cycle: res.cleanse_cycle,
        });
      }
      setMessages((m) => [...m, ...newBubbles]);
      if (res.context_panel) setContextPanel(res.context_panel);

      if (res.cleanse_cycle) {
        toast.success("Cleanse Cycle · " + res.cleanse_cycle.broadcast, {
          description: `Pulse #${res.cleanse_cycle.pulse} · DOCKING_COMPLETE`,
        });
      }

      if (res.anchor_blocked) {
        toast.warning("Taurian Anchor engaged", {
          description: `Blocked at ${res.anchor_layer}.`,
        });
        setLastEvent(`Anchor · ${res.anchor_layer} layer · blocked`);
      } else if (res.truth_drift) {
        toast.warning("Truth Tether · verification requested", {
          description: "Sentinel refused to guess.",
        });
        setLastEvent(`Truth Tether · unverified claim flagged`);
      } else if (res.drift_detected) {
        toast.error("Obsidian Sink neutralized drift", {
          description: res.drift_flags.join(", "),
        });
        setLastEvent(`Sink · neutralized: ${res.drift_flags.join(", ")}`);
      } else if (res.pulse_triggered) {
        toast.success("Self-Correction Pulse fired", {
          description: `Pulse #${res.pulse_count} · ${res.session_tokens_total} tokens`,
        });
        setLastEvent(`Pulse · #${res.pulse_count} @ ${res.session_tokens_total} tokens`);
      } else {
        setLastEvent(`Clean exchange · ${res.tokens} tokens`);
      }

      await refreshTelemetry();
    } catch (err) {
      if (err?.response?.status === 423) {
        toast.error("Sentinel · SAFE MODE", {
          description: "Architect handshake required to continue.",
        });
        await refreshTelemetry();
      } else if (
        err?.response?.status === 402 ||
        err?.response?.data?.detail?.code === "insufficient_aegis_credits"
      ) {
        // Console-gate: user is out of AEGIS. Surface a clear CTA to
        // the deposit page instead of a raw detail dump.
        const detail = err?.response?.data?.detail || {};
        const bal = detail.balance ?? 0;
        const need = detail.required ?? 1;
        toast.error("Console requires AEGIS credits", {
          description: `Balance: ${bal} · Need: ${need} per Q&A. Fund your console to continue.`,
          duration: 12000,
          action: {
            label: "Deposit AEGIS",
            onClick: () => {
              window.location.href = "/console/deposit";
            },
          },
        });
      } else {
        toast.error("Sentinel uplink failed", {
          description:
            typeof err?.response?.data?.detail === "string"
              ? err.response.data.detail
              : err?.response?.data?.detail?.message || err.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Voice IN · MediaRecorder → Whisper ─────────────────────────────────
  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        if (blob.size === 0) {
          toast.error("No audio captured", { description: "Try again and hold the mic for a second." });
          return;
        }
        try {
          setTranscribing(true);
          const { text } = await transcribeVoice(blob);
          if (text) {
            setInput((prev) => (prev ? prev + " " + text : text));
          }
        } catch (e) {
          toast.error("Transcription failed", {
            description: e?.response?.data?.detail || e.message,
          });
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone unavailable", {
        description: e.message || "Allow microphone access in your browser settings.",
      });
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    setRecording(false);
  };

  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  // Voice OUT — when speakReplies is on, speak the latest assistant
  // message aloud using the browser's built-in SpeechSynthesis.
  const lastSpokenIdRef = useRef(null);
  useEffect(() => {
    if (!speakReplies) return;
    if (!("speechSynthesis" in window)) return;
    const last = [...messages].reverse().find((m) => m.role === "assistant" && m.text);
    if (!last || last.id === lastSpokenIdRef.current) return;
    lastSpokenIdRef.current = last.id;
    try {
      const utter = new SpeechSynthesisUtterance(last.text.slice(0, 1200));
      utter.rate = 1.0;
      utter.pitch = 1.0;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    } catch {
      /* speech synthesis not available — silently no-op */
    }
  }, [messages, speakReplies]);

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-92px)]" data-testid="console-shell">
      {tier === null ? (
        <div
          className="flex-1 flex items-center justify-center px-6 py-12 bg-[#0a0a0c] text-zinc-400 text-xs uppercase tracking-[0.3em]"
          data-testid="console-tier-gate"
        >
          Resolving handshake…
        </div>
      ) : (
        <>
        <section className="flex-1 flex flex-col bg-[#0a0a0c] relative min-h-[calc(100vh-92px)] lg:min-h-0 w-full overflow-hidden">
        {/* Safe Mode banner */}
        {aegis?.safe_mode && (
          <div
            className="border-b border-red-500/60 bg-red-500/10 px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="safe-mode-banner"
          >
            <div className="flex items-center gap-2 text-red-400">
              <ShieldAlert className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.3em] font-body">
                Safe Mode · Layer 0 tripped
              </span>
            </div>
            <div className="text-xs text-zinc-300 flex-1 truncate">
              {aegis.reason || "Constitutional Integrity alert."}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="safe-mode-release-input"
                type="password"
                value={releaseCode}
                onChange={(e) => setReleaseCode(e.target.value)}
                placeholder={isArchitect ? "access_code" : "Architect only"}
                disabled={!isArchitect}
                className="bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-2 font-body text-zinc-100 placeholder:text-zinc-600 disabled:opacity-50 w-full sm:w-48"
              />
              <button
                onClick={handleRelease}
                disabled={releasing || !isArchitect}
                title={isArchitect ? "Release Safe Mode" : "Architect only"}
                data-testid="safe-mode-release-btn"
                className="px-4 py-2 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-emerald-400 disabled:cursor-not-allowed text-[10px] uppercase tracking-[0.3em] whitespace-nowrap"
              >
                {!isArchitect ? "Sealed" : releasing ? "…" : "Release"}
              </button>
            </div>
          </div>
        )}
        {/* Session header */}
        <div className="border-b border-zinc-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 bg-[#09090b]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Handshake · Session
            </div>
            <div
              className="font-body text-xs text-zinc-300 mt-0.5 truncate"
              data-testid="session-id-display"
            >
              {sessionId}
            </div>
          </div>
          <div className="hidden sm:block text-[10px] uppercase tracking-[0.35em] text-emerald-400 whitespace-nowrap">
            · 528 Hz · armed ·
          </div>
          {/* Wallet · v19.40.7 · pop-out for AEGIS balance + quick top-up
              v19.40.12 · live spendable-AEGIS chip so users can glance-check
              their balance without opening the dialog. */}
          <button
            type="button"
            onClick={() => setWalletOpen(true)}
            data-testid="open-wallet-btn"
            title={
              user
                ? `Wallet · ${walletSpendable ?? 0} spendable AEGIS · click to deposit or convert`
                : "Wallet · view balance and deposit AEGIS"
            }
            className="group inline-flex items-center gap-2 px-3 py-1.5 border border-amber-500/50 text-[10px] uppercase tracking-[0.3em] text-amber-300 hover:bg-amber-500 hover:text-zinc-950 transition-colors shrink-0"
          >
            <Wallet className="h-3 w-3" />
            Wallet
            {user && (
              <span
                data-testid="wallet-chip-balance"
                className="ml-1 -mr-1 px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/40 text-amber-200 group-hover:bg-zinc-950/20 group-hover:text-zinc-950 group-hover:border-zinc-950/30 tabular-nums font-mono text-[10px] tracking-normal"
              >
                {walletLoading && walletSpendable == null
                  ? "…"
                  : Number(walletSpendable ?? 0).toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
              </span>
            )}
          </button>
          {/* Clear visible history — visual declutter, Sentinel's memory unaffected */}
          <button
            type="button"
            onClick={() => {
              if (messages.length === 0) return;
              const lastId = messages[messages.length - 1]?.id;
              setHideBeforeId(lastId || null);
              setHistoryExpanded(false);
              toast.success("Visible history cleared", {
                description: "The Sentinel still remembers — only the screen was decluttered.",
              });
            }}
            disabled={messages.length <= 1}
            data-testid="clear-visible-history-btn"
            title="Hide previous messages from view (Sentinel's memory unaffected)"
            className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-800 hover:border-emerald-500 text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-800 disabled:hover:text-zinc-400 transition-colors shrink-0"
          >
            <Eraser className="h-3 w-3" />
            Clear view
          </button>
          {/* Speak replies aloud — uses browser SpeechSynthesis, no extra cost */}
          <button
            type="button"
            onClick={() => {
              const next = !speakReplies;
              setSpeakReplies(next);
              if (!next && "speechSynthesis" in window) {
                window.speechSynthesis.cancel();
              }
            }}
            data-testid="speak-replies-toggle"
            title={speakReplies ? "Voice replies ON — tap to mute" : "Voice replies OFF — tap to hear the Sentinel speak"}
            className={`hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] uppercase tracking-[0.3em] transition-colors shrink-0 ${
              speakReplies
                ? "border-emerald-500 text-emerald-400 bg-emerald-500/5"
                : "border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400"
            }`}
          >
            <Mic className="h-3 w-3" />
            Voice {speakReplies ? "on" : "off"}
          </button>
          {/* Mobile: telemetry toggle */}
          <button
            type="button"
            onClick={() => setTelemetryOpen(true)}
            data-testid="open-telemetry-btn"
            className="lg:hidden p-2 border border-zinc-800 text-emerald-400 hover:border-emerald-500 shrink-0"
            aria-label="Show telemetry"
          >
            <Gauge className="h-4 w-4" />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 sm:py-8 space-y-4"
          data-testid="message-list"
        >
          {(() => {
            // Filter out anything the operator has explicitly cleared,
            // then split the remainder into "older" / "recent" so we can
            // collapse the old tail when the conversation grows long.
            let visible = messages;
            if (hideBeforeId) {
              const hideIdx = messages.findIndex((m) => m.id === hideBeforeId);
              if (hideIdx >= 0) visible = messages.slice(hideIdx);
            }
            const shouldCollapse =
              visible.length > AUTO_COLLAPSE_THRESHOLD && !historyExpanded;
            const splitAt = shouldCollapse ? visible.length - VISIBLE_RECENT : 0;
            const older = shouldCollapse ? visible.slice(0, splitAt) : [];
            const recent = shouldCollapse ? visible.slice(splitAt) : visible;

            return (
              <>
                {shouldCollapse && (
                  <div className="flex justify-center" data-testid="history-collapse-banner">
                    <button
                      type="button"
                      onClick={() => {
                        setHistoryExpanded(true);
                        setHideBeforeId(null);
                      }}
                      data-testid="show-earlier-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-800 hover:border-emerald-500 text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-emerald-400 transition-colors"
                    >
                      <ChevronUp className="h-3 w-3" />
                      Show {older.length} earlier exchange{older.length === 1 ? "" : "s"}
                    </button>
                  </div>
                )}
                {!shouldCollapse && historyExpanded && visible.length > AUTO_COLLAPSE_THRESHOLD && (
                  <div className="flex justify-center" data-testid="history-expand-banner">
                    <button
                      type="button"
                      onClick={() => setHistoryExpanded(false)}
                      data-testid="hide-earlier-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-800 hover:border-emerald-500 text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-emerald-400 transition-colors"
                    >
                      <ChevronDown className="h-3 w-3" />
                      Hide earlier exchanges
                    </button>
                  </div>
                )}
                {recent.map((m) => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    onRephrase={(prompt) => sendVision(prompt, prompt)}
                    onOptionClick={(label) => sendMessage(label)}
                  />
                ))}
              </>
            );
          })()}
          {loading && (
            <div className="flex justify-start" data-testid="loading-indicator">
              <div className="border border-zinc-800 bg-[#0b0b0d] p-4 flex items-center gap-3">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                <span className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                  Routing through Obsidian Sink…
                </span>
              </div>
            </div>
          )}
        </div>

        <div
          className="border-t border-zinc-800 bg-[#09090b] px-3 sm:px-4 pt-3"
          data-testid="context-chip-slot"
        >
          {messages.length <= 1 && !loading && (
            <div
              className="mb-3 flex flex-wrap gap-2"
              data-testid="starter-prompts"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 w-full sm:w-auto sm:mr-2 flex items-center">
                Try asking ·
              </span>
              {STARTER_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => sendMessage(p)}
                  data-testid={`starter-prompt-${i}`}
                  className="px-3 py-2 text-[11px] text-zinc-300 border border-zinc-800 hover:border-emerald-500 hover:text-emerald-400 transition-colors font-body"
                >
                  {p}
                </button>
              ))}
            </div>
          )}
          <ContextChip sessionId={sessionId} context={contextPanel} />
        </div>

        <form
          onSubmit={submit}
          className="bg-[#09090b] p-3 sm:p-4 flex flex-col gap-2 pb-14 sm:pb-4 w-full overflow-hidden"
          data-testid="chat-form"
        >
          {attachments.length > 0 && (
            <AttachmentPicker
              attachments={attachments}
              setAttachments={setAttachments}
              max={3}
              disabled={loading}
              variant="chat"
              testId="chat-attachment-thumbs"
              hideButton
            />
          )}
          <div className="flex flex-row items-end gap-2 sm:gap-3 w-full">
            <div className="relative flex-1 min-w-0">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                rows={1}
                placeholder="Speak to the Sentinel… · tap the mic to dictate · attach an image any time"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm pl-12 pr-12 py-3 font-body resize-none text-zinc-100 placeholder:text-zinc-600 min-h-[48px] max-h-32 sm:min-h-[56px]"
              />
              <div className="absolute left-2 bottom-2 sm:bottom-3">
                <AttachmentPicker
                  attachments={attachments}
                  setAttachments={setAttachments}
                  max={3}
                  disabled={loading}
                  variant="chat"
                  testId="chat-attachment"
                  compact
                  hideThumbs
                />
              </div>
              <button
                type="button"
                onClick={toggleRecording}
                disabled={loading || transcribing}
                data-testid="voice-record-btn"
                title={recording ? "Stop recording" : "Speak to the Sentinel"}
                aria-label={recording ? "Stop recording" : "Start voice input"}
                aria-pressed={recording}
                className={`absolute right-2 bottom-2 sm:bottom-3 p-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  recording
                    ? "border-rose-500 text-rose-400 bg-rose-500/10 animate-pulse"
                    : transcribing
                    ? "border-emerald-500 text-emerald-400"
                    : "border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400"
                }`}
              >
                {transcribing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : recording ? (
                  <Square className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </button>
            </div>
            <button
              type="submit"
              data-testid="send-message-button"
              disabled={loading || !input.trim()}
              aria-label="Transmit"
              className="shrink-0 inline-flex items-center justify-center gap-2 w-12 sm:w-auto sm:px-6 py-3 min-h-[48px] sm:min-h-[56px] border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:border-zinc-800 disabled:text-zinc-700 disabled:hover:bg-transparent transition-colors text-xs uppercase tracking-[0.3em]"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Transmit</span>
            </button>
          </div>
        </form>
      </section>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <SentinelConsole
          stats={stats}
          globalStatus={globalStatus}
          lastEvent={lastEvent}
        />
      </div>

      {/* Mobile drawer */}
      {telemetryOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          data-testid="mobile-telemetry-drawer"
        >
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setTelemetryOpen(false)}
          />
          <div className="relative ml-auto h-full w-[88%] max-w-[360px] bg-[#0a0a0c] border-l border-zinc-800 overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-[#09090b] sticky top-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Telemetry
              </div>
              <button
                onClick={() => setTelemetryOpen(false)}
                data-testid="close-telemetry-btn"
                className="p-1.5 border border-zinc-800 text-zinc-400"
                aria-label="Close telemetry"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SentinelConsole
              stats={stats}
              globalStatus={globalStatus}
              lastEvent={lastEvent}
              variant="mobile"
            />
          </div>
        </div>
      )}
        </>
      )}

      {/* v19.40.7 · Wallet pop-out · AEGIS balance + quick converter + deposit CTA */}
      <Dialog open={walletOpen} onOpenChange={setWalletOpen}>
        <DialogContent
          data-testid="wallet-dialog"
          className="bg-[#0a0a0d] border border-amber-500/40 text-zinc-100 max-w-md shadow-[0_0_60px_rgba(245,158,11,0.15)]"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-[0.1em] flex items-center gap-2 text-amber-200">
              <Wallet className="h-4 w-4" /> Wallet
            </DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
              AEGIS balance · quick top-up
            </DialogDescription>
          </DialogHeader>

          {!user ? (
            <div className="border border-zinc-800 bg-[#0b0b0d] p-4 text-sm text-zinc-400 leading-relaxed">
              Sign in to see your AEGIS balance.
              <div className="mt-3">
                <Link
                  to="/signin"
                  data-testid="wallet-sign-in"
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-amber-300 hover:text-amber-100"
                >
                  Sign in <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Balances */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-emerald-500/30 bg-emerald-500/[0.04] p-3">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-400/80 font-mono">
                    Console credits
                  </div>
                  <div
                    className="mt-2 font-mono text-3xl tabular-nums text-emerald-200"
                    data-testid="wallet-credits"
                  >
                    {walletLoading ? "—" : (walletBalance ?? 0)}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                    spend on Q&amp;A
                  </div>
                </div>
                <div className="border border-amber-500/30 bg-amber-500/[0.04] p-3">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-amber-400/80 font-mono">
                    Spendable AEGIS
                  </div>
                  <div
                    className="mt-2 font-mono text-3xl tabular-nums text-amber-200"
                    data-testid="wallet-spendable-aegis"
                  >
                    {walletLoading ? "—" : (walletSpendable ?? 0)}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                    earned on chain
                  </div>
                </div>
              </div>

              {/* Quick converter · AEGIS → console credits */}
              <div className="mt-4 border border-zinc-800 bg-[#0b0b0d] p-3">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 font-mono mb-2 flex items-center gap-1.5">
                  <Coins className="h-3 w-3 text-amber-300" />
                  Convert earned AEGIS → credits
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={walletConvertAmt}
                    onChange={(e) => setWalletConvertAmt(e.target.value)}
                    data-testid="wallet-convert-amount"
                    className="flex-1 bg-black border border-zinc-800 focus:border-amber-500/60 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono"
                    placeholder="10"
                  />
                  <button
                    type="button"
                    onClick={convertAegisToCredits}
                    disabled={walletConverting || walletLoading}
                    data-testid="wallet-convert-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-amber-500/60 text-amber-200 hover:bg-amber-500 hover:text-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {walletConverting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ArrowRight className="h-3 w-3" />
                    )}
                    Convert
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-zinc-500 font-mono leading-relaxed">
                  1 AEGIS = 1 console credit. Debits from your earned AEGIS,
                  credits immediately available for Q&amp;A.
                </div>
              </div>

              {/* Deposit CTA · full flow lives on /deposit */}
              <Link
                to="/deposit"
                onClick={() => setWalletOpen(false)}
                data-testid="wallet-deposit-cta"
                className="mt-4 group flex items-center justify-between px-4 py-3 border border-sky-500/40 hover:border-sky-400 bg-sky-500/[0.04] transition-colors"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-mono">
                    Need more AEGIS?
                  </div>
                  <div className="text-sm text-zinc-100 mt-1 leading-tight">
                    Deposit via USDC · PayPal · Stripe
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-sky-400 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
